import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, inr } from "../../lib/api";
import { Card, Loading, Empty, StatusBadge, inputCls, btnPrimary, btnSecondary } from "../../components/admin/common";
import { ArrowLeft, Search, Download, FileSpreadsheet, X, GraduationCap } from "lucide-react";

const STATUSES = ["pending", "completed", "rejected", "duplicate"];
const EMPTY = { programs: [], levels: [], batches: [], statuses: [] };
const SEARCH_FIELDS = [["name", "Student Name"], ["email", "Email ID"], ["transaction", "Transaction ID"]];
const PLACEHOLDER = { name: "Search student name...", email: "Search email ID...", transaction: "Search transaction ID..." };

function CheckGroup({ title, options, selected, onToggle, onAll, onClear, testid }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{title}</span>
        <span className="flex gap-2 text-[11px]">
          <button onClick={onAll} className="text-blue-600 hover:underline" data-testid={`${testid}-all`}>All</button>
          <button onClick={onClear} className="text-slate-400 hover:underline" data-testid={`${testid}-clear`}>Clear</button>
        </span>
      </div>
      <div className="max-h-36 overflow-y-auto space-y-1 pr-1 border border-slate-200 rounded-lg p-2 bg-slate-50">
        {options.length === 0 && <div className="text-xs text-slate-400">None</div>}
        {options.map((o) => (
          <label key={o.value} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={selected.includes(o.value)} onChange={() => onToggle(o.value)}
              data-testid={`${testid}-opt`} className="rounded border-slate-300 text-blue-600" />
            <span className="truncate">{o.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function SchoolStudents() {
  const { sid } = useParams();
  const navigate = useNavigate();
  const [school, setSchool] = useState(null);
  const [options, setOptions] = useState({ programs: [], levels: [], batches: [] });
  const [draft, setDraft] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [search, setSearch] = useState("");
  const [searchBy, setSearchBy] = useState("name");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/schools/${sid}/breakdown`).then(({ data }) => {
      setSchool(data.school);
      const progs = new Map(), lvls = new Map(), bts = new Map();
      data.blocks.forEach((b) => {
        if (b.program_id) progs.set(b.program_id, b.program_name);
        if (b.level_id) lvls.set(b.level_id, b.level_name);
        b.batches.forEach((bt) => { if (bt.batch_id) bts.set(bt.batch_id, bt.batch_name); });
      });
      setOptions({
        programs: [...progs].map(([value, label]) => ({ value, label })),
        levels: [...lvls].map(([value, label]) => ({ value, label })),
        batches: [...bts].map(([value, label]) => ({ value, label })),
      });
    }).catch(() => {});
  }, [sid]);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (applied.programs.length) p.set("program_id", applied.programs.join(","));
    if (applied.levels.length) p.set("level_id", applied.levels.join(","));
    if (applied.batches.length) p.set("batch_id", applied.batches.join(","));
    if (applied.statuses.length) p.set("payment_status", applied.statuses.join(","));
    if (search) { p.set("search", search); p.set("search_by", searchBy); }
    setData(null);
    api.get(`/schools/${sid}/student-list?${p}`).then((r) => setData(r.data)).catch(() => setData({ count: 0, students: [] }));
  }, [sid, applied, search, searchBy]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const toggle = (key, value) => setDraft((d) => ({ ...d, [key]: d[key].includes(value) ? d[key].filter((x) => x !== value) : [...d[key], value] }));
  const setAll = (key, all) => setDraft((d) => ({ ...d, [key]: all }));
  const apply = () => setApplied(draft);
  const reset = () => { setDraft(EMPTY); setApplied(EMPTY); setSearch(""); setSearchBy("name"); };

  const labelFor = (key, val) => (options[key] || STATUSES.map((s) => ({ value: s, label: s }))).find?.((o) => o.value === val)?.label || val;
  const chips = [
    ...applied.programs.map((v) => ({ key: "programs", v, label: `Program: ${labelFor("programs", v)}` })),
    ...applied.levels.map((v) => ({ key: "levels", v, label: `Level: ${labelFor("levels", v)}` })),
    ...applied.batches.map((v) => ({ key: "batches", v, label: `Batch: ${labelFor("batches", v)}` })),
    ...applied.statuses.map((v) => ({ key: "statuses", v, label: `Status: ${v}` })),
  ];
  const removeChip = (c) => { const next = { ...applied, [c.key]: applied[c.key].filter((x) => x !== c.v) }; setApplied(next); setDraft(next); };

  const exportFile = (fmt) => {
    const p = new URLSearchParams();
    if (applied.programs.length) p.set("program_id", applied.programs.join(","));
    if (applied.levels.length) p.set("level_id", applied.levels.join(","));
    if (applied.batches.length) p.set("batch_id", applied.batches.join(","));
    if (applied.statuses.length) p.set("payment_status", applied.statuses.join(","));
    if (search) { p.set("search", search); p.set("search_by", searchBy); }
    p.set("fmt", fmt);
    window.open(`${API}/schools/${sid}/student-list/export?${p}`, "_blank");
  };

  return (
    <div>
      <button onClick={() => navigate("/admin/schools")} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm mb-4"><ArrowLeft className="w-4 h-4" /> Back to Schools</button>
      <div className="flex items-center gap-2 mb-1"><GraduationCap className="w-6 h-6 text-blue-600" /><h1 className="font-head text-2xl font-bold text-slate-900">{school?.name || "School"} — Students</h1></div>
      <p className="text-sm text-slate-500 mb-5" data-testid="completed-only-note">Showing only students whose payment is <span className="font-semibold text-emerald-600">100% completed</span>.</p>

      <Card className="p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <CheckGroup title="Program" testid="filter-program" options={options.programs} selected={draft.programs}
            onToggle={(v) => toggle("programs", v)} onAll={() => setAll("programs", options.programs.map((o) => o.value))} onClear={() => setAll("programs", [])} />
          <CheckGroup title="Level" testid="filter-level" options={options.levels} selected={draft.levels}
            onToggle={(v) => toggle("levels", v)} onAll={() => setAll("levels", options.levels.map((o) => o.value))} onClear={() => setAll("levels", [])} />
          <CheckGroup title="Batch" testid="filter-batch" options={options.batches} selected={draft.batches}
            onToggle={(v) => toggle("batches", v)} onAll={() => setAll("batches", options.batches.map((o) => o.value))} onClear={() => setAll("batches", [])} />
          <CheckGroup title="Payment Status" testid="filter-status" options={STATUSES.map((s) => ({ value: s, label: s }))} selected={draft.statuses}
            onToggle={(v) => toggle("statuses", v)} onAll={() => setAll("statuses", STATUSES)} onClear={() => setAll("statuses", [])} />
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-4 pt-4 border-t border-slate-100">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input className={`${inputCls} pl-10 pr-9`} placeholder={PLACEHOLDER[searchBy]} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="student-search" />
            {search && <button onClick={() => setSearch("")} data-testid="student-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>}
          </div>
          <select className={`${inputCls} sm:w-48`} value={searchBy} onChange={(e) => setSearchBy(e.target.value)} data-testid="student-search-by" aria-label="Search By">
            {SEARCH_FIELDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button className={btnPrimary} onClick={apply} data-testid="apply-filter-btn">Apply Filter</button>
          <button className={btnSecondary} onClick={reset} data-testid="reset-filter-btn"><X className="w-4 h-4" /> Reset</button>
        </div>
        {chips.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid="filter-chips">
            {chips.map((c, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-100 rounded-full px-3 py-1 text-xs font-medium">
                {c.label}<button onClick={() => removeChip(c)} className="hover:text-blue-900"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}
      </Card>

      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-slate-600" data-testid="students-found">Completed Students <span className="font-head font-bold text-slate-900 text-base ml-1">{data?.count ?? 0}</span></div>
        <div className="flex gap-2">
          <button className={btnSecondary} onClick={() => exportFile("xlsx")} data-testid="export-xlsx-btn"><FileSpreadsheet className="w-4 h-4" /> Excel</button>
          <button className={btnSecondary} onClick={() => exportFile("csv")} data-testid="export-csv-btn"><Download className="w-4 h-4" /> CSV</button>
        </div>
      </div>

      <Card>
        {data === null ? <Loading /> : data.students.length === 0 ? <Empty label="No students match these filters" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
                {["Student ID", "Student", "Parent / Mobile", "Program / Level / Batch", "Registration ID", "Required", "Paid", "Remaining", "Pmts", "Status"].map((h) => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}
              </tr></thead>
              <tbody>
                {data.students.map((s) => (
                  <tr key={s.student_id} className="border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer" onClick={() => navigate(`/admin/students/${s.student_id}`)} data-testid="school-student-row">
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{(s.student_id || "").slice(0, 8).toUpperCase()}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">{s.student_name}</td>
                    <td className="px-4 py-3 text-slate-600">{s.parent_name}<div className="text-xs text-slate-400 font-mono">{s.mobile}</div></td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{s.program_name}<div className="text-slate-400">{s.level_name} · {s.batch_name}</div></td>
                    <td className="px-4 py-3 font-mono text-xs text-blue-600">{s.registration_id}</td>
                    <td className="px-4 py-3">{inr(s.required)}</td>
                    <td className="px-4 py-3 text-emerald-700 font-medium">{inr(s.total_paid)}</td>
                    <td className="px-4 py-3">{inr(s.remaining)}</td>
                    <td className="px-4 py-3 text-center">{s.num_payments}</td>
                    <td className="px-4 py-3"><StatusBadge status={s.payment_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
